/**
 * TimelineSlider Component - Premiere Pro Style with Mobile Support
 * Dual-handle range slider for timeline control with draggable range bar
 * Features: Left handle (IN point), Right handle (OUT point), Middle bar drag
 * Mobile: Touch-optimized with 44px minimum tap targets
 */

import React, { useRef, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { MIN_TOUCH_TARGET } from './constants';

interface TimelineSliderProps {
  startDate: Date;
  endDate: Date;
  minDate: Date;
  maxDate: Date;
  onRangeChange: (startDate: Date, endDate: Date) => void;
  isMobile?: boolean;
  isSmallMobile?: boolean;
}

type DragMode = 'left' | 'right' | 'middle' | null;

export const TimelineSlider: React.FC<TimelineSliderProps> = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  onRangeChange,
  isMobile = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartLeft, setDragStartLeft] = useState(0);
  const [dragStartRight, setDragStartRight] = useState(0);
  const [isHoveringLeft, setIsHoveringLeft] = useState(false);
  const [isHoveringRight, setIsHoveringRight] = useState(false);
  const [isHoveringMiddle, setIsHoveringMiddle] = useState(false);

  // Responsive handle size
  // Desktop: 16px (original), Mobile: 22px (50% reduction from 44px)
  const handleWidth = isMobile ? MIN_TOUCH_TARGET / 2 : 16;
  const handleHeight = isMobile ? MIN_TOUCH_TARGET : 40;

  /**
   * Convert date to position percentage (0-100)
   */
  const dateToPercent = useCallback((date: Date): number => {
    const totalMs = maxDate.getTime() - minDate.getTime();
    const dateMs = date.getTime() - minDate.getTime();
    return Math.max(0, Math.min(100, (dateMs / totalMs) * 100));
  }, [minDate, maxDate]);

  /**
   * Convert position percentage to date
   */
  const percentToDate = useCallback((percent: number): Date => {
    const totalMs = maxDate.getTime() - minDate.getTime();
    const dateMs = minDate.getTime() + (percent / 100) * totalMs;
    return new Date(dateMs);
  }, [minDate, maxDate]);

  /**
   * Format date for display
   */
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /**
   * Calculate current positions
   */
  const leftPercent = dateToPercent(startDate);
  const rightPercent = dateToPercent(endDate);
  const rangeWidth = rightPercent - leftPercent;

  /**
   * Handle mouse/touch down on left handle (IN point)
   */
  const handleLeftMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragMode('left');
    const clientX = 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
    setDragStartX(clientX);
    setDragStartLeft(leftPercent);
  };

  /**
   * Handle mouse/touch down on right handle (OUT point)
   */
  const handleRightMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragMode('right');
    const clientX = 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
    setDragStartX(clientX);
    setDragStartRight(rightPercent);
  };

  /**
   * Handle mouse/touch down on middle bar (move entire range)
   */
  const handleMiddleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragMode('middle');
    const clientX = 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
    setDragStartX(clientX);
    setDragStartLeft(leftPercent);
    setDragStartRight(rightPercent);
  };

  /**
   * Unified handler for mouse/touch move during drag
   */
  const handleMove = useCallback((clientX: number) => {
    if (!dragMode || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = rect.width - 30; // Subtract 15px padding on each side
    const deltaX = clientX - dragStartX;
    const deltaPercent = (deltaX / containerWidth) * 100;

    if (dragMode === 'left') {
      // Drag left handle
      const newLeftPercent = Math.max(0, Math.min(dragStartLeft + deltaPercent, rightPercent - 5));
      const newStartDate = percentToDate(newLeftPercent);

      // Ensure minimum range (1 day)
      if (endDate.getTime() - newStartDate.getTime() >= 86400000) {
        onRangeChange(newStartDate, endDate);
      }
    } else if (dragMode === 'right') {
      // Drag right handle
      const newRightPercent = Math.max(leftPercent + 5, Math.min(100, dragStartRight + deltaPercent));
      const newEndDate = percentToDate(newRightPercent);

      // Ensure minimum range (1 day)
      if (newEndDate.getTime() - startDate.getTime() >= 86400000) {
        onRangeChange(startDate, newEndDate);
      }
    } else if (dragMode === 'middle') {
      // Drag middle bar (move entire range)
      const rangeDuration = dragStartRight - dragStartLeft;
      let newLeftPercent = dragStartLeft + deltaPercent;
      let newRightPercent = dragStartRight + deltaPercent;

      // Clamp to boundaries
      if (newLeftPercent < 0) {
        newLeftPercent = 0;
        newRightPercent = rangeDuration;
      } else if (newRightPercent > 100) {
        newRightPercent = 100;
        newLeftPercent = 100 - rangeDuration;
      }

      const newStartDate = percentToDate(newLeftPercent);
      const newEndDate = percentToDate(newRightPercent);
      onRangeChange(newStartDate, newEndDate);
    }
  }, [dragMode, dragStartX, dragStartLeft, dragStartRight, leftPercent, rightPercent, startDate, endDate, onRangeChange, percentToDate]);

  /**
   * Handle mouse move during drag
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    handleMove(e.clientX);
  }, [handleMove]);

  /**
   * Handle touch move during drag
   */
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length > 0 && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  }, [handleMove]);

  /**
   * Handle mouse up (end drag)
   */
  const handleMouseUp = useCallback(() => {
    setDragMode(null);
  }, []);

  /**
   * Set up global mouse and touch event listeners
   */
  React.useEffect(() => {
    if (dragMode) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      document.body.style.cursor = dragMode === 'middle' ? 'grabbing' : 'ew-resize';
      document.body.style.userSelect = 'none';

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [dragMode, handleMouseMove, handleTouchMove, handleMouseUp]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        px: 2,
        py: 1.5,
        backgroundColor: '#f5f5f5', // MD3 light surface
        borderRadius: 1,
        border: '1px solid #e0e0e0', // MD3 light outline
      }}
    >
      {/* Date Range Display */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: 16, color: '#616161' }} />
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8125rem',
              color: '#1f1f1f', // MD3 light on-surface
              fontWeight: 500,
            }}
          >
            {formatDate(startDate)}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            color: '#616161', // MD3 light on-surface-variant
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Timeline Range
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8125rem',
              color: '#1f1f1f', // MD3 light on-surface
              fontWeight: 500,
            }}
          >
            {formatDate(endDate)}
          </Typography>
          <CalendarIcon sx={{ fontSize: 16, color: '#616161' }} />
        </Box>
      </Box>

      {/* Timeline Slider Container */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          height: 48,
          backgroundColor: '#e8eaed', // MD3 light surface-variant
          borderRadius: 1,
          border: '1px solid #c4c7c5', // MD3 light outline-variant
          overflow: 'hidden',
          px: '15px', // 15px padding on left and right
        }}
      >
        {/* Full Timeline Background Rail */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '15px',
            right: '15px',
            height: 8,
            transform: 'translateY(-50%)',
            backgroundColor: '#c4c7c5', // MD3 light outline-variant
            borderRadius: 1,
          }}
        />

        {/* Selected Range Bar (Premiere Pro Blue) */}
        <Box
          onMouseDown={handleMiddleMouseDown}
          onTouchStart={handleMiddleMouseDown}
          onMouseEnter={() => setIsHoveringMiddle(true)}
          onMouseLeave={() => setIsHoveringMiddle(false)}
          sx={{
            position: 'absolute',
            top: '50%',
            left: `calc(15px + (100% - 30px) * ${leftPercent / 100})`,
            width: `calc((100% - 30px) * ${rangeWidth / 100})`,
            height: isMobile ? MIN_TOUCH_TARGET : 20,
            transform: 'translateY(-50%)',
            backgroundColor: dragMode === 'middle' || isHoveringMiddle ? '#60a5fa' : '#3b82f6',
            borderRadius: 0.5,
            cursor: 'grab',
            transition: 'background-color 0.15s ease',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
            '&:active': {
              cursor: 'grabbing',
            },
            zIndex: 1,
          }}
        >
          {/* Range Duration Display */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '0.6875rem',
              fontWeight: 700,
              color: '#ffffff',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))} days
          </Box>
        </Box>

        {/* Left Handle (IN Point) */}
        <Box
          onMouseDown={handleLeftMouseDown}
          onTouchStart={handleLeftMouseDown}
          onMouseEnter={() => setIsHoveringLeft(true)}
          onMouseLeave={() => setIsHoveringLeft(false)}
          sx={{
            position: 'absolute',
            top: '50%',
            left: `calc(15px + (100% - 30px) * ${leftPercent / 100})`,
            width: handleWidth,
            height: handleHeight,
            transform: 'translate(-50%, -50%)',
            backgroundColor: dragMode === 'left' || isHoveringLeft ? '#fbbf24' : '#f59e0b',
            borderRadius: 1,
            cursor: 'ew-resize',
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
            boxShadow: dragMode === 'left' || isHoveringLeft
              ? '0 0 0 3px rgba(251, 191, 36, 0.3), 0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 2px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 3,
              height: 20,
              backgroundColor: '#ffffff',
              borderRadius: 0.5,
              opacity: 0.9,
            },
          }}
        >
          {/* IN Label */}
          <Typography
            sx={{
              position: 'absolute',
              bottom: -24,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.625rem',
              fontWeight: 700,
              color: '#f59e0b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)', // Softer shadow for light theme
            }}
          >
            IN
          </Typography>
        </Box>

        {/* Right Handle (OUT Point) */}
        <Box
          onMouseDown={handleRightMouseDown}
          onTouchStart={handleRightMouseDown}
          onMouseEnter={() => setIsHoveringRight(true)}
          onMouseLeave={() => setIsHoveringRight(false)}
          sx={{
            position: 'absolute',
            top: '50%',
            left: `calc(15px + (100% - 30px) * ${rightPercent / 100})`,
            width: handleWidth,
            height: handleHeight,
            transform: 'translate(-50%, -50%)',
            backgroundColor: dragMode === 'right' || isHoveringRight ? '#fbbf24' : '#f59e0b',
            borderRadius: 1,
            cursor: 'ew-resize',
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
            boxShadow: dragMode === 'right' || isHoveringRight
              ? '0 0 0 3px rgba(251, 191, 36, 0.3), 0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 2px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 3,
              height: 20,
              backgroundColor: '#ffffff',
              borderRadius: 0.5,
              opacity: 0.9,
            },
          }}
        >
          {/* OUT Label */}
          <Typography
            sx={{
              position: 'absolute',
              bottom: -24,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.625rem',
              fontWeight: 700,
              color: '#f59e0b',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)', // Softer shadow for light theme
            }}
          >
            OUT
          </Typography>
        </Box>
      </Box>

      {/* Instruction Text */}
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.6875rem',
          color: '#616161', // MD3 light on-surface-variant
          textAlign: 'center',
          fontStyle: 'italic',
        }}
      >
        Drag handles to adjust range • Drag bar to move entire selection
      </Typography>
    </Box>
  );
};

export default TimelineSlider;
